import React, { useState } from 'react';
import { Heart, History, Users, PlayCircle, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import ContentCard from '../components/ContentCard';
import { ContinueWatchingRow } from '../components/HomeSections';
import { PageHeader, Chips, Grid, EmptyState } from '../components/PageBits';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useApp } from '../context/AppContext';
import { useCatalog } from '../context/CatalogContext';

export default function MyList() {
  const { myList, following, history, clearHistory, toggleFollow, user } = useApp();
  const { byId } = useCatalog();
  const [filter, setFilter] = useState('All');
  const items = myList.map(byId).filter(Boolean).filter((c) => filter === 'All' || (filter === 'Movies' && c.type === 'movie') || (filter === 'Shows' && c.type === 'show') || (filter === 'Podcasts' && c.type === 'podcast'));
  const followed = following.map(byId).filter(Boolean);
  const hist = history.map((h) => ({ ...h, content: byId(h.contentId) })).filter((h) => h.content);
  const tabCls = 'data-[state=active]:bg-[#15121B] data-[state=active]:shadow-sm data-[state=active]:text-[#FFFFFF] text-[#C7C4CB] rounded-lg px-4 h-9 gap-2 text-[13px]';

  return (
    <Layout>
      <PageHeader title="My List" subtitle={user ? 'Everything you have saved, followed and watched.' : 'Sign in to save titles, follow podcasts and keep your watch history.'} right={!user && <a href="/signin" className="btn-brand !h-10">Sign In</a>} />
      <Tabs defaultValue="list" className="px-[22px] pb-10">
        <TabsList className="bg-[#15121B] border border-[#26212F] h-11 p-1 mb-5">
          <TabsTrigger value="list" className={tabCls} data-testid="tab-my-list"><Heart size={14} /> My List <span className="text-[11px] text-[#8A8694]">{myList.length}</span></TabsTrigger>
          <TabsTrigger value="cw" className={tabCls} data-testid="tab-continue"><PlayCircle size={14} /> Continue Watching</TabsTrigger>
          <TabsTrigger value="history" className={tabCls} data-testid="tab-history"><History size={14} /> Watch History</TabsTrigger>
          <TabsTrigger value="following" className={tabCls} data-testid="tab-following"><Users size={14} /> Following <span className="text-[11px] text-[#8A8694]">{following.length}</span></TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Chips options={['All', 'Movies', 'Shows', 'Podcasts']} value={filter} onChange={setFilter} className="mb-4" testId="mylist-filter" />
          {items.length ? <div className="-mx-[22px]"><Grid>{items.map((c) => <ContentCard key={c.id} item={c} className="!w-full" />)}</Grid></div> : <div className="-mx-[22px]"><EmptyState icon={Heart} title="Your list is empty" text="Tap the + on any movie, show or podcast to save it here." /></div>}
        </TabsContent>
        <TabsContent value="cw"><div className="-ml-[22px]"><ContinueWatchingRow title="" limit={0} showRemove /></div></TabsContent>
        <TabsContent value="history">
          {hist.length ? (
            <div className="surface divide-y divide-[#26212F]">
              <div className="flex justify-end p-2"><button onClick={clearHistory} className="text-[12px] text-[#C7C4CB] hover:text-[#FFFFFF] flex items-center gap-1"><Trash2 size={12} /> Clear history</button></div>
              {hist.map((h, i) => (
                <div key={i} className="flex items-center gap-4 p-3">
                  <img src={h.content.image} alt="" className="w-24 h-14 rounded-md object-cover" />
                  <div className="min-w-0 flex-1"><div className="text-[14px] font-semibold">{h.content.title}</div><div className="text-[12px] text-[#8A8694]">{h.episodeId ? `Episode ${h.episodeId.split('-').pop().toUpperCase()}` : h.content.type} &bull; {new Date(h.watchedAt).toLocaleString()}</div></div>
                </div>
              ))}
            </div>
          ) : <div className="-mx-[22px]"><EmptyState icon={History} title="No history yet" text="Titles you watch will appear here." /></div>}
        </TabsContent>
        <TabsContent value="following">
          {followed.length ? (
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              {followed.map((p) => (
                <div key={p.id} className="surface p-3 flex items-center gap-3">
                  <img src={p.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1"><div className="text-[14px] font-semibold truncate">{p.title}</div><div className="text-[12px] text-[#8A8694]">{p.host}</div></div>
                  <button onClick={() => toggleFollow(p.id)} className="chip on h-8">Following</button>
                </div>
              ))}
            </div>
          ) : <div className="-mx-[22px]"><EmptyState icon={Users} title="Not following anyone" text="Follow podcasts to get new episodes here." /></div>}
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
